export const Communication_Service = {
    publicSpeakingEtiquette: {
        title: "Public Speaking & Etiquette",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/publicSpeakingTitle.png",
        body: `
          <section>
            <h3>🎤 Lead With Confidence, Represent With Warmth</h3>
            <p>Whether you’re giving a tour, leading an event, or sharing updates with clients — <span class="highlight">how you speak reflects who we are</span>. Your words shape first impressions, trust, and the emotional tone of every interaction.</p>
          </section>
      
          <section>
            <h4>🗣️ Speaking Presence</h4>
            <ul>
              <li>👀 <span class="highlight-blue">Make soft, welcoming eye contact</span> — scan the room naturally</li>
              <li>🧍‍♀️ <span class="highlight-blue">Stand with grounded posture</span> — no slouching or pacing</li>
              <li>🔊 <span class="highlight">Use a calm, steady voice</span> — clear and loud enough for the space</li>
              <li>🎯 <span class="highlight-yellow">Avoid filler words</span> (“like,” “um,” “kinda”) — pause with intention</li>
            </ul>
          </section>
      
          <section>
            <h4>📚 Delivering Your Message</h4>
            <ul>
              <li>🧠 <span class="highlight">Stick to 3 key points</span> — more than that overwhelms</li>
              <li>🖼️ <span class="highlight-green">Use stories or metaphors</span> — people remember feelings, not facts</li>
              <li>❓ <span class="highlight-blue">Leave space for questions</span> — it shows confidence and care</li>
              <li>🙌 <span class="highlight-yellow">Validate the audience’s effort</span> or curiosity</li>
            </ul>
          </section>
      
          <section>
            <h4>💬 Tone & Language</h4>
            <ul>
              <li>🧩 <span class="highlight">Use inclusive, positive phrasing</span> (“We like to…” instead of “You should…”)</li>
              <li>🎯 Speak to the group — but <span class="highlight-blue">respond to individuals with care</span></li>
              <li>🌱 <span class="highlight-green">Normalize mistakes</span> — “Every pet is different, and that’s okay”</li>
              <li>🧠 <span class="highlight-yellow">Avoid jargon</span> — translate dog/cat behavior into everyday language</li>
            </ul>
          </section>
      
          <section>
            <h4>🎭 Common Pitfalls to Avoid</h4>
            <ul>
              <li>❌ Speaking too softly or trailing off</li>
              <li>❌ Sounding too rehearsed or robotic</li>
              <li>❌ Talking too fast — especially when nervous</li>
              <li>❌ Correcting or scolding in front of others</li>
            </ul>
          </section>
      
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p><em>During a client tour, Mia notices a pup getting a spa bath. She smiles and says, “This is Cookie — she’s one of our VIPs and loves warm water days.” The clients laugh, relax, and start asking questions. Mia’s tone turns a walk-through into a warm, trusted experience.</em></p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/publicSpeakingRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Great speakers don’t aim to impress — they aim to connect.</span> Speak like you're welcoming someone into your home. That’s the Pawsible tone.</p>
          </section>
        `
    },      
    empathyClarity: {
        title: "Communicating With Empathy & Clarity",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/communicatingWithEmpathyTitle.png",
        body: `
          <section>
            <h3>💛 Speak to Connect, Not Just Inform</h3>
            <p>Great communication is more than being nice — it’s being clear, compassionate, and grounded in care. <span class="highlight">When clients and teammates feel understood, they feel safe.</span></p>
          </section>
    
          <section>
            <h4>🎯 The Pawsible Communication Style</h4>
            <ul>
              <li>🗣️ <strong>Be clear, not clinical:</strong> Avoid robotic or overly formal language</li>
              <li>🤝 <strong>Validate first:</strong> “That sounds stressful” goes a long way</li>
              <li>🧠 <strong>Give context, not just commands:</strong> Explain the why behind what we do</li>
            </ul>
            <p><span class="highlight-blue">Always ask yourself: Would I feel reassured by this message?</span></p>
          </section>
    
          <section>
            <h4>💬 Real-World Examples</h4>
            <ul>
              <li>❌ <strong>Not this:</strong> “Your dog was reactive today.”<br>✅ <strong>Try this:</strong> “We noticed Max barked when meeting new dogs today. That’s normal, and we gave him more space to decompress.”</li>
              <li>❌ <strong>Not this:</strong> “We had to move your cat.”<br>✅ <strong>Try this:</strong> “Luna seemed overwhelmed in the main room, so we gave her a cozy corner crate where she settled beautifully.”</li>
            </ul>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/communicatingWithEmpathyRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-yellow">Specific language builds trust. Vague language builds doubt.</span></p>
          </section>
    
          <section>
            <h4>🧭 Internal Communication</h4>
            <ul>
              <li>📝 Use clear subject lines in dashboard logs and notes</li>
              <li>👥 Share concerns early — silence can lead to safety risks</li>
              <li>📌 Use the tag system (urgent, behavior, medical) to help others prioritize</li>
            </ul>
            <p><span class="highlight">What’s obvious to you may be crucial for someone else.</span> Always write with the next shift in mind.</p>
          </section>
    
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Clear + kind = confident care.</span> When we communicate well, we make people feel supported — and that’s where trust begins.</p>
          </section>
        `
    },
    handlingDifficultClients: {
        title: "Handling Difficult Situations With Pet Parents",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/handlingClientsTitle.png",
        body: `
        <section>
            <h3>🧘‍♀️ Stay Grounded, Stay Kind</h3>
            <p>
            Challenging moments with clients don’t define us — <span class="highlight">how we respond to them does</span>. At Pawsible, every conversation is a chance to show empathy, professionalism, and emotional intelligence.
            </p>
        </section>
    
        <section>
            <h4>🚩 Recognizing Tricky Moments</h4>
            <ul>
            <li>💢 A parent feels blindsided by an update</li>
            <li>⏰ They're frustrated about timing or communication</li>
            <li>❓ They question our care or policies</li>
            </ul>
            <p>
            <span class="highlight-yellow">If your heart rate spikes, it’s a sign to slow down and breathe.</span>
            </p>
        </section>
    
        <section>
            <h4>💬 De-escalation First, Explanation Second</h4>
            <ul>
            <li>💛 <strong>Validate the emotion:</strong> “I hear that you’re upset — and I care that you feel this way.”</li>
            <li>📘 <strong>Ground your voice and body language:</strong> Calm tone, steady pace, relaxed posture</li>
            <li>📖 <strong>Then explain with clarity:</strong> “Here’s what happened, and here’s how we’re addressing it.”</li>
            </ul>
            <p>
            <span class="highlight-blue">If you go in defensive, they won’t hear your care — only your wall.</span>
            </p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/handlingClientsEscalate.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
        </section>
    
        <section>
            <h4>📞 When to Escalate</h4>
            <ul>
            <li>📍 Client is yelling, recording, or threatening reviews</li>
            <li>🧭 You feel emotionally triggered or unsure what to say</li>
            <li>💼 Client insists on speaking with leadership</li>
            </ul>
            <p><span class="highlight-red">Escalation isn't failure — it’s smart delegation when emotions are high.</span></p>
        </section>
    
        <section>
            <h4>🧠 Language That Protects Trust</h4>
            <ul>
            <li>❌ “That’s not what happened.”<br>✅ <span class="highlight">“Let me walk you through what we observed.”</span></li>
            <li>❌ “We always do it this way.”<br>✅ <span class="highlight-blue">“Here’s our usual approach — but we’re flexible based on your pet’s needs.”</span></li>
            <li>❌ “We’ve never had a problem before.”<br>✅ <span class="highlight-green">“Thank you for sharing this — your feedback helps us grow.”</span></li>
            </ul>
        </section>
    
        <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p><em>A client picks up their pup and says, “Why wasn’t I told she skipped lunch?” Instead of reacting, Jordan says, “Totally fair question — I would want to know, too. She seemed a little tired today, so we held off and planned to text you. I’m so glad you asked so we could explain.” The client relaxes and thanks them.</em></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/handlingClientsRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
        </section>
    
        <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Hard moments are still connection moments.</span> Stay calm. Stay human. Let your empathy lead — and let the protocols support you.</p>
        </section>
        `
    },      
    wowMoments: {
        title: "Creating WOW Moments at Drop-off & Pick-up",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/wowMomentsTitle.png",
        body: `
          <section>
            <h3>🎉 Small Moments, Big Impressions</h3>
            <p>
              The first and last 30 seconds of every visit shape how clients feel about their entire experience. <span class="highlight">Drop-off and pick-up are our greatest opportunities to build emotional loyalty.</span>
            </p>
          </section>
    
          <section>
            <h4>🌞 At Drop-off: Create Calm & Trust</h4>
            <ul>
              <li>🫶 <strong>Welcome with presence:</strong> Smile, say their pet’s name, and engage warmly</li>
              <li>📋 <strong>Reassure:</strong> “You’re all set — we’ve got [Pet Name] today!”</li>
              <li>👀 <strong>Observe:</strong> Check the pet’s mood and energy — it shows attentiveness</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/wowMomentsDropOff.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-blue">A confident start reduces client anxiety and improves pet transitions.</span></p>
          </section>
    
          <section>
            <h4>🎁 At Pick-up: Deliver the “Wow”</h4>
            <ul>
              <li>📸 <strong>Share a mini story:</strong> “Bella loved the water spritz game today — total mermaid energy!”</li>
              <li>🧼 <strong>Send them out fresh:</strong> Quick brush or spritz before hand-off if time allows</li>
              <li>📝 <strong>Include a personal note:</strong> “He was nervous at first but warmed up fast — great progress!”</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/wowMomentsPickUp.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-yellow">Clients want to feel that we *know* their pet — not just watched them.</span></p>
          </section>
    
          <section>
            <h4>✨ Bonus Touches That Stick</h4>
            <ul>
              <li>🎨 Daily photos or themed updates</li>
              <li>🎓 Encouragement message after training milestones</li>
              <li>🎂 Birthday surprises — a hat, treat, or little moment of celebration</li>
            </ul>
            <p><span class="highlight-green">Delight creates loyalty. Details create trust.</span></p>
          </section>
    
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p><em>A parent picks up Leo and looks tired. Ana softly says, “He had a blast with the ball pit today. He’s all zonked out — might beat you to the couch tonight.” The client smiles wide, visibly relaxes, and thanks Ana for the update and vibe. That’s the WOW.</em></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/wowMomentsRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
    
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">The best customer service moments are the most human.</span> Personal. Simple. Sincere. That’s what clients remember — and why they come back.</p>
          </section>
        `
    },
    peerFeedback: {
        title: "How to Give and Receive Peer Feedback",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/peerFeedbackTitle.png",
        body: `
          <section>
            <h3>🧭 Feedback Is a Gift — Treat It That Way</h3>
            <p>
              At Pawsible, we don’t avoid feedback — we <span class="highlight">normalize it, welcome it, and use it to grow</span>. Great teams don’t wait for reviews — they check in with each other early and often.
            </p>
          </section>
    
          <section>
            <h4>🤲 Giving Feedback That Lands</h4>
            <ul>
              <li>🧠 <strong>Stick to behavior, not personality:</strong> “I noticed you skipped the crate wipe today” not “You’re always careless.”</li>
              <li>💡 <strong>Be timely and calm:</strong> Within the day, not days later — and not mid-shift rush</li>
              <li>📌 <strong>Use specific examples:</strong> This helps it feel constructive, not vague or personal</li>
              <li>🗣️ <strong>Lead with care:</strong> “I care about the team — can I share something I noticed?”</li>
            </ul>
            <p><span class="highlight-blue">Good feedback feels respectful, direct, and helpful — not harsh or hidden.</span></p>
          </section>
    
          <section>
            <h4>👂 Receiving Feedback Like a Pro</h4>
            <ul>
              <li>🤝 <strong>Listen without interrupting</strong> — even if you disagree</li>
              <li>🧘 <strong>Regulate your reaction</strong> — breathe before you defend</li>
              <li>✅ <strong>Ask questions:</strong> “What would better look like next time?”</li>
              <li>🌱 <strong>Say thank you — and mean it</strong></li>
            </ul>
            <p><span class="highlight-green">Feedback is a sign of respect. Silence isn’t peace — it’s avoidance.</span></p>
          </section>
    
          <section>
            <h4>🚩 When to Bring in a Lead</h4>
            <ul>
              <li>📉 The same issue keeps repeating without change</li>
              <li>💢 Tension feels personal or unresolved</li>
              <li>🧭 You need clarity, backup, or emotional support</li>
            </ul>
            <p><span class="highlight-yellow">Team leads are here to mediate — not to take sides, but to help move forward.</span></p>
          </section>
    
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p><em>Jules notices that Maya didn’t log an incident. Instead of gossiping, she checks in: “Hey, I saw something with Max earlier. Want me to help add it to the log?” Maya appreciates the nudge, logs it, and says thanks. No ego, no shame — just teamwork.</em></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/peerFeedbackRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
    
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">We rise faster when we lift each other honestly.</span> If it helps the team, say it. If someone says it to you, receive it with openness. That’s how Pawsible grows strong.</p>
          </section>
        `
    },
    teamHuddleProtocol: {
        title: "Team Huddles: Daily Communication Flow",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/huddleProtocolTitle.png",
        body: `
          <section>
            <h3>📣 Stay in Sync, Move as One</h3>
            <p>
              A strong team isn’t just reactive — it’s <span class="highlight">proactively aligned</span>. At Pawsible, daily communication keeps every shift grounded in clarity, compassion, and care.
            </p>
          </section>
    
          <section>
            <h4>🧭 What’s a Huddle?</h4>
            <p>
              A “huddle” is a short, structured check-in — used to align on pet needs, shift tasks, and updates. It happens <span class="highlight-yellow">before opening</span>, during mid-day transitions, or after major incidents.
            </p>
            <ul>
              <li>⏱️ 5–10 minutes max</li>
              <li>📌 Led by floor lead or manager</li>
              <li>📍 Takes place in the break area or designated check-in zone</li>
            </ul>
          </section>
    
          <section>
            <h4>🧵 Team Chat = Our Ongoing Huddle</h4>
            <ul>
              <li>💬 <strong>#daily-shift-log:</strong> Morning lead posts the overview; all team members react to confirm</li>
              <li>🗂️ <strong>#incident-reports:</strong> Logged ASAP with photos or context (Team Chat is for tracking, not debating)</li>
              <li>📣 <strong>#general-updates:</strong> For daily wins, kudos, and FYIs</li>
            </ul>
            <p><span class="highlight-blue">If it’s not logged, it didn’t happen. If it’s in Team Chat, it’s shared knowledge.</span></p>
          </section>
    
          <section>
            <h4>🧠 What to Cover in a Huddle</h4>
            <ul>
              <li>🐾 Pet-specific notes (anxious dogs, medical needs, behavior alerts)</li>
              <li>🧼 Task assignments & zone coverage</li>
              <li>🎉 Announcements: birthdays, new staff, reminders</li>
              <li>📦 Supplies running low or equipment updates</li>
            </ul>
            <p><span class="highlight-green">No surprises. No scrambling. Everyone knows the game plan.</span></p>
          </section>
    
          <section>
            <h4>📏 Huddle Etiquette</h4>
            <ul>
              <li>👂 Be present — no phones or side convos</li>
              <li>✍️ Take mental or physical notes</li>
              <li>🤝 Ask questions, not just “all good” nods</li>
            </ul>
            <p><span class="highlight-yellow">Respect the time. Respect the team. Huddles aren’t optional — they’re how we work smarter.</span></p>
          </section>
    
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p><em>Before a big weekend shift, Taylor leads the morning huddle: “We’ve got 6 new clients, 3 birthday flags, and Jasper needs med at noon. Let’s keep the back crate room calm for newbies. Questions?” Everyone nods — shift flows like butter.</em></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/huddleProtocolRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
    
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">Strong communication builds smooth shifts — and strong culture.</span> Don’t skip it. Don’t rush it. A great huddle = a great day at Pawsible.</p>
          </section>
        `
    },
    internalConflictResolution: {
        title: "Conflict Resolution Between Staff Members",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/conflictResolutionTitle.png",
        body: `
          <section>
            <h3>🤝 We Don’t Avoid — We Repair</h3>
            <p>
              Conflict is natural in any team — especially one that cares deeply. <span class="highlight">What matters is how we handle it.</span> At Pawsible, resolution means restoring trust, not just avoiding tension.
            </p>
          </section>
    
          <section>
            <h4>🔍 Spotting the Signs</h4>
            <ul>
              <li>😶 Silence or avoidance between coworkers</li>
              <li>💬 Passive-aggressive comments or cold tone</li>
              <li>🚨 Breakdowns in collaboration or dropped handoffs</li>
            </ul>
            <p><span class="highlight-yellow">If something feels off — it probably is. Don’t wait for it to explode.</span></p>
          </section>
    
          <section>
            <h4>🧘 Step 1: Self-Regulate First</h4>
            <ul>
              <li>🧠 Pause — don’t speak from anger</li>
              <li>🫁 Breathe and reflect — what do you need, not just feel?</li>
              <li>📝 Write it down if needed — clarity helps calm</li>
            </ul>
            <p><span class="highlight-blue">You don’t have to fix it alone — but you do need to enter with respect.</span></p>
          </section>
    
          <section>
            <h4>💬 Step 2: Address It Directly (If Safe)</h4>
            <ul>
              <li>🕊️ Use “I” language: “I felt frustrated when…”</li>
              <li>🎯 Focus on the moment — not everything from the past month</li>
              <li>🧩 Ask for a mutual solution, not an apology</li>
            </ul>
            <p><span class="highlight-green">Most tension deflates when we feel heard, not blamed.</span></p>
          </section>
    
          <section>
            <h4>🛟 Step 3: Ask a Lead for Support</h4>
            <ul>
              <li>📍 If the issue feels bigger than one convo</li>
              <li>📩 If you’ve tried and it didn’t help</li>
              <li>🧭 If you need help translating emotion into action</li>
            </ul>
            <p><span class="highlight-red">This isn’t tattling — it’s team repair. Leads mediate, not punish.</span></p>
          </section>
    
          <section>
            <h4>🌟 Real-Life Scenario</h4>
            <p><em>Ellie notices that Sam has been short with her all morning. Instead of snapping back, she checks in later: “Hey, are we okay? I noticed a weird tone earlier and I want to clear it if I can.” Sam apologizes, shares she’s just tired — tension gone, trust restored.</em></p>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/conflictResolutionRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
    
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">Caring for pets starts with caring for our team.</span> Don’t let silence fester. Speak kindly, repair quickly, and lead with heart — that’s the Pawsible way.</p>
          </section>
        `
    }
}